<template>
    <div class="spark-page">
        <!-- Loading State -->
        <div v-if="!spark" class="loading-container">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
            <p class="mt-3 text-muted">Initializing Spark configuration...</p>
        </div>

        <!-- Main Content -->
        <div v-else class="spark-content">
            <!-- Page Header -->
            <div class="page-header mb-4">
                <div class="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 class="mb-1">
                            <b-icon icon="lightning-charge-fill" class="text-warning mr-2"></b-icon>
                            Create New Spark
                        </h2>
                        <p class="text-muted mb-0">
                            Configure your serverless function
                        </p>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <b-tabs content-class="mt-4" nav-class="spark-tabs">
                <!-- General Tab -->
                <b-tab active>
                    <template #title>
                        <b-icon icon="gear-fill" class="mr-2"></b-icon>
                        {{ $t('general') }}
                    </template>

                    <b-row>
                        <!-- Configuration Column -->
                        <b-col cols="12" md="6" class="mb-4">
                            <!-- Basic Configuration Card -->
                            <b-card class="config-card shadow-sm">
                                <template #header>
                                    <h5 class="mb-0">
                                        <b-icon icon="sliders" class="mr-2"></b-icon>
                                        Configuration
                                    </h5>
                                </template>

                                <b-form-group
                                    :label="$t('name')"
                                    label-class="font-weight-semibold"
                                >
                                    <protokol-invalid-feedback :errors="validator.fieldErrors('name')">
                                        <b-input v-model="name" placeholder="my-spark-function" />
                                    </protokol-invalid-feedback>
                                </b-form-group>

                                <b-form-group
                                    :label="$t('runtime')"
                                    label-class="font-weight-semibold"
                                >
                                    <v-select 
                                        :options="runtimes" 
                                        v-model="spark.runtime"
                                        :clearable="false"
                                        class="custom-v-select"
                                        placeholder="Select runtime"
                                    />
                                </b-form-group>

                                <b-form-group
                                    :label="$t('memory')"
                                    :description="$t('sparkMemoryDescription')"
                                    label-class="font-weight-semibold"
                                >
                                    <v-select 
                                        :options="availableMemory" 
                                        v-model="spark.memory"
                                        :clearable="false"
                                        class="custom-v-select"
                                    />
                                </b-form-group>
                            </b-card>
                        </b-col>

                        <!-- Info Column -->
                        <b-col cols="12" md="6" class="mb-4">
                            <b-card class="info-card shadow-sm">
                                <template #header>
                                    <h5 class="mb-0">
                                        <b-icon icon="info-circle" class="mr-2"></b-icon>
                                        Getting Started
                                    </h5>
                                </template>

                                <div class="info-content">
                                    <div class="info-item mb-3">
                                        <b-icon icon="check-circle-fill" variant="success" class="mr-2"></b-icon>
                                        <span>Configure your function name and runtime</span>
                                    </div>
                                    <div class="info-item mb-3">
                                        <b-icon icon="check-circle-fill" variant="success" class="mr-2"></b-icon>
                                        <span>Edit your handler and package.json in the Code tab</span>
                                    </div>
                                    <div class="info-item mb-3">
                                        <b-icon icon="check-circle-fill" variant="success" class="mr-2"></b-icon>
                                        <span>Click Save to deploy your Spark function</span>
                                    </div>

                                    <b-alert show variant="info" class="mt-4 mb-0">
                                        <b-icon icon="lightbulb" class="mr-2"></b-icon>
                                        <small>After creation, you can manage versions and environment variables in the edit view.</small>
                                    </b-alert>
                                </div>

                                <template #footer>
                                    <b-button @click="save" variant="success" size="lg" block class="save-btn">
                                        <b-icon icon="plus-circle" class="mr-2"></b-icon>
                                        {{ $t('create') }}
                                    </b-button>
                                </template>
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>

                <!-- Code Tab -->
                <b-tab>
                    <template #title>
                        <b-icon icon="file-code" class="mr-2"></b-icon>
                        Code
                    </template>

                    <b-row>
                        <!-- Handler Editor -->
                        <b-col cols="12" xl="6" class="mb-4">
                            <b-card v-if="handlerExpression" class="code-card shadow-sm h-100">
                                <template #header>
                                    <h5 class="mb-0">
                                        <b-icon icon="file-code" class="mr-2"></b-icon>
                                        Handler
                                    </h5>
                                </template>
                                <CodeEditor 
                                    v-model="handlerExpression" 
                                    :withButton="false" 
                                    :controls="false"
                                    :height="700"
                                />
                            </b-card>
                        </b-col>

                        <!-- Package Editor -->
                        <b-col cols="12" xl="6" class="mb-4">
                            <b-card v-if="packageExpression" class="code-card shadow-sm h-100">
                                <template #header>
                                    <h5 class="mb-0">
                                        <b-icon icon="box-seam" class="mr-2"></b-icon>
                                        Package
                                    </h5>
                                </template>
                                <CodeEditor 
                                    v-model="packageExpression" 
                                    :withButton="false" 
                                    :controls="false"
                                    :height="700"
                                />
                            </b-card>
                        </b-col>
                    </b-row>
                </b-tab>
            </b-tabs>
        </div>
    </div>
</template>
<script>
import CodeEditor from '@/components/form/Editor/Code'
import { get } from 'lodash'
import vSelect from 'vue-select'

export default {
    name: 'Spark',
    components: {
        CodeEditor,
        vSelect,
    },
    data: function() {
        return {
            validator: this.$validator(),
            runtimes: ["node18", "node20", "node22"],
            availableMemory: [128, 256, 512],
            spark: {
                name: "",
                lang: null,
                settings: [
                    {
                        version: "0.1.0",
                        dist: {
                            handler: `exports.main = async (req, res) => {
    res.send({
        hello: "world"
    })
}`,
                            package: `{
  "name": "spark-function",
  "version": "1.0.0",
  "description": "Spark Function",
  "main": "handler.js",
  "scripts": {
    "test": "echo \\"Error: no test specified\\" && exit 0"
  },
  "keywords": [],
  "author": "protokol.io",
  "license": "MIT"
}`
                        }
                    }
                ]
            },
            ideVersion: "0.1.0",
        }
    },

    watch: {
        spark: {
            handler: function() {
                this.validator.revalidate()
            },

            deep: true
        },
    },

    computed: {
        name: {
            get() {
                return this.spark.name.toLowerCase()
            },

            set(val) {
                this.spark.name = val.toLowerCase()
            }
        },

        handlerExpression: {
            get() {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                return get(settings, 'dist.handler', null)
            },

            set(val) {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                settings.dist.handler = val
            }
        },

        packageExpression: {
            get() {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                return get(settings, 'dist.package', null)
            },

            set(val) {
                let settings = this.spark.settings.find(s => {
                    return s.version == this.ideVersion
                })

                settings.dist.package = val
            }
        }
    },
    methods: {
        save() {
            if (!this.validator.pass()) return

            let popup = this.$loadingPopup()
            this.$sandbox.spark().create(this.spark).then((resp) => {
                this.$router.push(`/admin/spark/${resp.data.uuid}`)
            }).catch(err => {
                console.error(err)
                
                if (err.response) {
                    if (err.response.status == 409) {
                        this.$bvToast.toast(this.$t('sparkLimitReached'), {
                            title: this.$t('error'),
                            variant: 'danger'
                        })

                        return
                    }
                }

                this.$bvToast.toast(this.$t('unexpectedError'), {
                    title: this.$t('error'),
                    variant: 'danger'
                })
            }).finally(() => {
                popup.hide()
            })
        }
    },

    created() {
        this.validator.validate(this.spark, {
            isValidDNSName: (model, errors) => {
                // regex to match if name has only - and _ and no numbers and can only be 18 chars long
                if (model.name.length > 18) {
                    errors.add('name', [this.$t('maxlength', { max: 18 })])
                }

                if (!model.name.match(/^[a-z][a-z\-_.]*[a-z]$/)) {
                    errors.add('name', [this.$t('notValid')])
                }
            },
        })
    }
}
</script>

<style scoped>
/* Main Container */
.spark-page {
    min-height: 100vh;
    padding: 1.5rem;
    background: var(--bg-main-start);
}

/* Loading State */
.loading-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 60vh;
    color: var(--text-secondary);
}

/* Page Header */
.page-header {
    background: var(--bg-card);
    padding: 2rem;
    border-radius: var(--radius-md);
    border: 1px solid var(--border-separator);
    box-shadow: var(--shadow-sm);
}

.page-header h2 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-primary);
    margin: 0;
}

.page-header .text-muted {
    color: var(--text-secondary) !important;
}

/* Tabs */
.spark-content >>> .spark-tabs {
    background: var(--bg-card);
    padding: 0.5rem 1rem 0;
    border-radius: var(--radius-md) var(--radius-md) 0 0;
    border: 1px solid var(--border-separator);
    border-bottom: none;
}

.spark-content >>> .nav-tabs .nav-link {
    color: var(--text-secondary);
    border: none;
    padding: 0.75rem 1.5rem;
    font-weight: 500;
    transition: var(--transition-quick);
}

.spark-content >>> .nav-tabs .nav-link:hover {
    color: var(--text-primary);
    background: var(--bg-card-secondary);
    border-radius: var(--radius-sm) var(--radius-sm) 0 0;
}

.spark-content >>> .nav-tabs .nav-link.active {
    color: var(--primary-start);
    background: var(--bg-card);
    border: none;
    border-bottom: 2px solid var(--primary-start);
    font-weight: 600;
}

/* Cards */
.config-card,
.info-card,
.code-card {
    background: var(--bg-card);
    border: 1px solid var(--border-separator);
    border-radius: var(--radius-md);
    overflow: hidden;
    transition: var(--transition-quick);
}

.config-card:hover,
.info-card:hover,
.code-card:hover {
    border-color: var(--border-default);
    box-shadow: var(--shadow-md) !important;
}

.config-card >>> .card-header,
.info-card >>> .card-header,
.code-card >>> .card-header {
    background: var(--bg-card-secondary);
    color: var(--text-primary);
    padding: 1rem 1.25rem;
    border-bottom: 1px solid var(--border-separator);
    font-weight: 600;
}

.config-card >>> .card-body,
.info-card >>> .card-body,
.code-card >>> .card-body {
    padding: 1.5rem;
    background: var(--bg-card);
}

.info-card >>> .card-footer {
    background: var(--bg-card-secondary);
    border-top: 1px solid var(--border-separator);
    padding: 1rem 1.25rem;
}

/* Info Content */
.info-content {
    color: var(--text-primary);
}

.info-item {
    display: flex;
    align-items: center;
    color: var(--text-secondary);
}

/* Form Elements */
.font-weight-semibold {
    font-weight: 600;
    color: var(--text-secondary);
    font-size: 0.875rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

/* Custom v-select styling */
.custom-v-select >>> .vs__dropdown-toggle {
    padding: 0.5rem;
    background: var(--bg-card-secondary);
    border-radius: var(--radius-sm);
    border: 1px solid var(--border-default);
    transition: var(--transition-quick);
}

.custom-v-select >>> .vs__dropdown-toggle:focus-within {
    border-color: var(--border-hover);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

.custom-v-select >>> .vs__selected {
    margin: 0;
    padding: 0.25rem 0.5rem;
    background: var(--primary-start);
    color: var(--text-on-button);
    border-radius: 4px;
}

.custom-v-select >>> .vs__search {
    padding: 0.25rem 0.5rem;
    color: var(--text-primary);
}

.custom-v-select >>> .vs__search::placeholder {
    color: var(--text-muted);
}

.custom-v-select >>> .vs__actions {
    padding: 0 0.5rem;
}

.custom-v-select >>> .vs__dropdown-menu {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
}

.custom-v-select >>> .vs__dropdown-option {
    color: var(--text-primary);
}

.custom-v-select >>> .vs__dropdown-option--highlight {
    background: var(--bg-card);
    color: var(--text-primary);
}

/* Buttons */
.save-btn {
    font-weight: 600;
    padding: 0.75rem;
    border-radius: var(--radius-sm);
    transition: var(--transition-quick);
}

.save-btn:hover {
    box-shadow: var(--shadow-success);
}

/* Code Editor Container */
.code-card >>> .CodeMirror {
    border-radius: var(--radius-sm);
    font-size: 14px;
    background: var(--bg-card-secondary);
    color: var(--text-primary);
}

/* Form Controls */
>>> .form-control,
>>> .form-select,
>>> input.form-control {
    background: var(--bg-card-secondary);
    border: 1px solid var(--border-default);
    color: var(--text-primary);
    border-radius: var(--radius-sm);
    transition: var(--transition-quick);
}

>>> .form-control:focus,
>>> .form-select:focus {
    background: var(--bg-card-secondary);
    border-color: var(--border-hover);
    color: var(--text-primary);
    box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
}

>>> .form-control::placeholder {
    color: var(--text-muted);
}

/* Alert */
>>> .alert-info {
    background: var(--bg-card-secondary);
    border-color: var(--color-info);
    color: var(--text-secondary);
}

/* Responsive Adjustments */
@media (max-width: 991px) {
    .page-header h2 {
        font-size: 1.5rem;
    }
    
    .spark-page {
        padding: 1rem;
    }
}

/* Animation */
@keyframes fadeIn {
    from {
        opacity: 0;
        transform: translateY(10px);
    }
    to {
        opacity: 1;
        transform: translateY(0);
    }
}

.spark-content {
    animation: fadeIn 0.3s ease;
}
</style>